import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { importBackup, importLeadsCsv, type ImportSummary } from '../../api/queries';
import { Modal } from '../ui/Modal';

export function ImportModal({ onClose }: { onClose: () => void }) {
  const csvRef = useRef<HTMLInputElement>(null);
  const backupRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [backupResult, setBackupResult] = useState<ImportSummary | null>(null);
  const qc = useQueryClient();

  const handleCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('Reading CSV…');
    const text = await file.text();
    setBusy('Importing…');
    try {
      const res = await importLeadsCsv(text);
      qc.invalidateQueries();
      alert(`Imported ${res.created} leads. ${res.failures.length} failed.`);
      onClose();
    } catch (err) {
      alert((err as Error).message);
    }
    setBusy(null);
  };

  const handleBackup = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('Reading backup…');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data?.leads)) {
        throw new Error('File does not look like a Figureit CRM backup (missing leads[]).');
      }
      setBusy(`Importing ${data.leads.length} leads…`);
      const res = await importBackup(data);
      qc.invalidateQueries();
      setBackupResult(res.summary);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      // Reset so re-picking the same file fires onChange again.
      if (backupRef.current) backupRef.current.value = '';
      setBusy(null);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Import data"
      footer={
        <button className="btn btn-secondary flex-1 sm:flex-none" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="mb-5">
        <h3 className="text-sm font-semibold mb-1">Import leads from CSV</h3>
        <p className="text-xs text-text3 mb-2">
          Headers expected: Company, Contact, Title, Email, Phone, Source, Stage, NRE Value, Deal
          Value Yearly, Priority, Owner Email, Next Action, Next Action Date, Notes.
        </p>
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={handleCsv} />
        <button className="btn btn-secondary" onClick={() => csvRef.current?.click()}>
          Choose CSV file
        </button>
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-semibold mb-1">Restore from backup</h3>
        <p className="text-xs text-text3 mb-2">
          For a Figureit CRM backup JSON. Syncs stages, activity types, custom fields, owners, and
          activities into your workspace. Re-runnable — leads matched by (company, contact) are
          updated in place, not duplicated.
        </p>
        <input ref={backupRef} type="file" accept=".json" hidden onChange={handleBackup} />
        <button
          className="btn btn-secondary"
          onClick={() => backupRef.current?.click()}
          disabled={busy != null}
        >
          Choose backup JSON file
        </button>
      </div>

      {busy && <div className="text-text2 text-sm mt-3">{busy}</div>}

      {backupResult && (
        <div className="mt-4 border border-border rounded-lg p-3 text-xs space-y-1.5 bg-bg/40">
          <div className="font-semibold text-sm text-text">Import complete</div>
          <div>
            Leads: <b>{backupResult.leadsInserted}</b> inserted, <b>{backupResult.leadsUpdated}</b>{' '}
            updated
          </div>
          <div>
            Activities: <b>{backupResult.activitiesInserted}</b> inserted,{' '}
            {backupResult.activitiesSkipped} already present
          </div>
          {backupResult.stagesCreated.length > 0 && (
            <div>+ stages: {backupResult.stagesCreated.join(', ')}</div>
          )}
          {backupResult.activityTypesCreated.length > 0 && (
            <div>+ activity types: {backupResult.activityTypesCreated.join(', ')}</div>
          )}
          {backupResult.customFieldsCreated.length > 0 && (
            <div>+ custom fields: {backupResult.customFieldsCreated.join(', ')}</div>
          )}
          {backupResult.customFieldOptionsExtended.map((e) => (
            <div key={e.label}>
              · extended "{e.label}" options: +[{e.added.join(', ')}]
            </div>
          ))}
          {backupResult.ownersCreated.length > 0 && (
            <div>
              + placeholder users: {backupResult.ownersCreated.map((o) => o.email).join(', ')}{' '}
              <span className="text-text3">(rename in Settings → Members)</span>
            </div>
          )}
          <div className="text-text3 pt-1">
            Totals in workspace: {backupResult.totals.leads} leads ·{' '}
            {backupResult.totals.activities} activities · {backupResult.totals.stages} stages ·{' '}
            {backupResult.totals.customFields} custom fields
          </div>
        </div>
      )}
    </Modal>
  );
}
